import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { updateEmployee } from 'services/employees';
import { getEmployeeBenefits, getCompanyBenefits, updateEmployeeBenefit } from 'services/benefits';
import { createNotification } from 'services/timeOff';
import { toast } from 'components/Toast';

const REASONS = [
    { value: 'voluntary',    label: 'Voluntary Resignation' },
    { value: 'involuntary',  label: 'Involuntary Termination' },
    { value: 'layoff',       label: 'Layoff / Reduction in Force' },
    { value: 'retirement',   label: 'Retirement' },
    { value: 'contract_end', label: 'End of Contract' },
    { value: 'other',        label: 'Other' },
];

const GRACE_PERIODS = [
    { value: 0,  label: 'Revoke Immediately',       desc: 'Access ends on the termination date.' },
    { value: 30, label: 'Keep Access for 30 Days',  desc: 'Employee can download paystubs & W-2 for 30 days.' },
    { value: 90, label: 'Keep Access for 90 Days',  desc: 'Recommended — employee can retrieve all tax documents.' },
];

const STEP_LABELS = ['Confirm', 'Final Pay', 'Benefits', 'Portal Access', 'Summary'];

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtMoney = (v) => v != null ? '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

function StepDots({ current, total }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
            {Array.from({ length: total }).map((_, i) => (
                <React.Fragment key={i}>
                    <div style={{
                        width: i === current ? 28 : 10, height: 10, borderRadius: 99,
                        background: i < current ? '#16a34a' : i === current ? '#E7730A' : '#e5e7eb',
                        transition: 'all 0.25s',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        {i < current && <span style={{ color: '#fff', fontSize: 8 }}>✓</span>}
                    </div>
                    {i < total - 1 && <div style={{ flex: 1, maxWidth: 28, height: 2, background: i < current ? '#16a34a' : '#e5e7eb' }} />}
                </React.Fragment>
            ))}
        </div>
    );
}

export default function TerminationModal({ emp, onClose, onDone }) {
    const queryClient = useQueryClient();
    const [step, setStep]               = useState(0);
    const [termDate, setTermDate]       = useState(new Date().toISOString().slice(0, 10));
    const [reason, setReason]           = useState('voluntary');
    const [notes, setNotes]             = useState('');
    const [deactivateBenefits, setDeactivateBenefits] = useState({});
    const [graceDays, setGraceDays]     = useState(30);
    const [saving, setSaving]           = useState(false);
    const [done, setDone]               = useState(false);
    const [results, setResults]         = useState(null);

    const { data: allEmpBenefits = [] } = useQuery({ queryKey: ['employee-benefits'], queryFn: getEmployeeBenefits, staleTime: 60000 });
    const { data: companyBenefits = [] } = useQuery({ queryKey: ['company-benefits'], queryFn: getCompanyBenefits, staleTime: 60000 });

    const empBenefits = useMemo(() => {
        const planMap = {};
        companyBenefits.forEach(p => { planMap[p.id] = p; });
        return allEmpBenefits
            .filter(eb => eb.employeeId === emp.id && eb.active !== false)
            .map(eb => ({ ...eb, planName: planMap[eb.companyBenefitId]?.description || planMap[eb.companyBenefitId]?.name || eb.description || eb.type || '—' }));
    }, [allEmpBenefits, companyBenefits, emp.id]);

    // Default all benefits to deactivate
    useMemo(() => {
        const init = {};
        empBenefits.forEach(eb => { init[eb.id] = true; });
        setDeactivateBenefits(init);
    }, [empBenefits.length]); // eslint-disable-line

    const cobraDeadline = useMemo(() => {
        const d = new Date(termDate);
        d.setDate(d.getDate() + 60);
        return fmt(d.toISOString());
    }, [termDate]);

    const portalExpiryDate = useMemo(() => {
        if (graceDays === 0) return fmt(termDate);
        const d = new Date(termDate);
        d.setDate(d.getDate() + graceDays);
        return fmt(d.toISOString());
    }, [termDate, graceDays]);

    const cobraBenefits = empBenefits.filter(eb => {
        const t = (eb.type || '').toLowerCase();
        return ['medical', 'dental', 'vision', 'health'].some(x => t.includes(x));
    });

    const empName = [emp.firstName, emp.lastName].filter(Boolean).join(' ');
    const companyName = localStorage.getItem('companyName') || 'Your Employer';

    const handleFinish = async () => {
        setSaving(true);
        const benefitsDeactivated = [];
        const benefitsFailed = [];
        try {
            // 1. Deactivate selected benefits
            for (const eb of empBenefits) {
                if (deactivateBenefits[eb.id]) {
                    try {
                        await updateEmployeeBenefit({ benefitId: eb.id, active: false });
                        benefitsDeactivated.push(eb.planName);
                    } catch {
                        benefitsFailed.push(eb.planName);
                    }
                }
            }

            // 2. Terminate employee — always set active: false
            const payload = {
                employeeId: emp.id,
                _companyId: localStorage.getItem('companyId'),
                terminationDate: termDate + 'T00:00:00',
                terminationReason: reason,
                active: false,
                ...(notes ? { terminationNotes: notes } : {}),
            };
            await updateEmployee(payload);

            // 3. Send in-app notification to the employee
            const accessMsg = graceDays === 0
                ? 'Your portal access has been revoked as of your termination date.'
                : `You can continue to access your Employee Portal for ${graceDays} days (until ${portalExpiryDate}) to download your paystubs and tax documents.`;

            try {
                await createNotification({
                    recipientId: emp.id,
                    recipientType: 'employee',
                    type: 'termination',
                    title: `Your employment at ${companyName} has ended`,
                    message: `Your last day of employment was ${fmt(termDate)}. ${accessMsg} Contact HR with any questions.`,
                    link: '/employee/paystubs',
                });
            } catch (_) {}

            // 4. Invalidate queries
            queryClient.invalidateQueries({ queryKey: ['employee', emp.id] });
            queryClient.invalidateQueries({ queryKey: ['employees'] });
            queryClient.invalidateQueries({ queryKey: ['employee-benefits'] });

            setResults({ benefitsDeactivated, benefitsFailed, graceDays, termDate, reason, portalExpiryDate });
            setStep(4);
            setDone(true);
            toast(`${empName} has been offboarded successfully.`, 'success');
        } catch (e) {
            toast(e?.response?.data?.message || e?.message || 'Termination failed. Please try again.', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }} onClick={e => { if (e.target === e.currentTarget && !saving) onClose(); }}>
            <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', overflow: 'hidden' }}>

                {/* Header */}
                <div style={{ background: 'linear-gradient(135deg, #7f1d1d, #b91c1c)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <div style={{ color: '#fca5a5', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Guided Offboarding</div>
                        <div style={{ color: '#fff', fontSize: 18, fontWeight: 700, marginTop: 2 }}>{empName}</div>
                    </div>
                    <button onClick={onClose} disabled={saving} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, color: '#fff', width: 32, height: 32, cursor: 'pointer', fontSize: 18 }}>×</button>
                </div>

                {/* Step indicator */}
                <div style={{ padding: '16px 24px 0' }}>
                    <StepDots current={step} total={STEP_LABELS.length} />
                    <div style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: -8, marginBottom: 12 }}>
                        Step {step + 1} of {STEP_LABELS.length} — <strong style={{ color: '#E7730A' }}>{STEP_LABELS[step]}</strong>
                    </div>
                </div>

                {/* Body */}
                <div style={{ padding: '0 24px 20px', maxHeight: '62vh', overflowY: 'auto' }}>

                    {/* STEP 0 — Confirm */}
                    {step === 0 && (
                        <div>
                            <p style={{ fontSize: 13, color: '#374151', marginBottom: 18 }}>
                                You are about to begin the offboarding process for <strong>{empName}</strong>. Please confirm the details below.
                            </p>
                            <div style={{ marginBottom: 14 }}>
                                <label style={labelStyle}>Termination Date <span style={{ color: '#dc2626' }}>*</span></label>
                                <input type="date" value={termDate} onChange={e => setTermDate(e.target.value)} style={inputStyle} />
                            </div>
                            <div style={{ marginBottom: 14 }}>
                                <label style={labelStyle}>Reason for Termination</label>
                                <select value={reason} onChange={e => setReason(e.target.value)} style={inputStyle}>
                                    {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                </select>
                            </div>
                            <div style={{ marginBottom: 14 }}>
                                <label style={labelStyle}>Internal Notes <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
                                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                                    placeholder="e.g. two-week notice given, equipment returned on last day..."
                                    rows={3} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
                            </div>
                            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#991b1b' }}>
                                ⚠️ The employee will be set to <strong>Inactive</strong> and removed from future payrolls. This can be reversed by editing the employee record.
                            </div>
                        </div>
                    )}

                    {/* STEP 1 — Final Pay */}
                    {step === 1 && (
                        <div>
                            <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
                                <div style={{ fontWeight: 700, color: '#1d4ed8', marginBottom: 4, fontSize: 13 }}>💰 Final Pay Reminder</div>
                                <p style={{ fontSize: 12, color: '#1e3a8a', margin: 0 }}>
                                    Most states require final pay on the last day of work or within a set number of days. Verify your state's requirements.
                                </p>
                            </div>
                            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px', marginBottom: 14 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 10 }}>Final Pay Checklist</div>
                                {[
                                    'Run an off-cycle payroll for any remaining hours or salary',
                                    'Include unused PTO payout if required by your state',
                                    'Include any severance pay if applicable',
                                    'Verify all expense reimbursements are processed',
                                ].map((item, i) => (
                                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: 12, color: '#374151' }}>
                                        <span style={{ color: '#9ca3af', flexShrink: 0 }}>□</span> {item}
                                    </div>
                                ))}
                            </div>
                            <div style={{ background: '#fefce8', border: '1px solid #fcd34d', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#92400e' }}>
                                💡 Go to <strong>Dashboard → Start New Payroll → Off-Cycle</strong> and select <strong>"Termination / Final Pay"</strong> as the reason.
                            </div>
                        </div>
                    )}

                    {/* STEP 2 — Benefits */}
                    {step === 2 && (
                        <div>
                            <p style={{ fontSize: 13, color: '#374151', marginBottom: 14 }}>
                                Select which benefits to deactivate. Benefits typically end on the termination date.
                            </p>
                            {empBenefits.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: 13 }}>
                                    No active benefits found for this employee.
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                                    {empBenefits.map(eb => (
                                        <label key={eb.id} style={{
                                            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                                            border: `1.5px solid ${deactivateBenefits[eb.id] ? '#fca5a5' : '#e5e7eb'}`,
                                            borderRadius: 10, cursor: 'pointer',
                                            background: deactivateBenefits[eb.id] ? '#fef2f2' : '#f9fafb',
                                            transition: 'all 0.15s',
                                        }}>
                                            <input type="checkbox"
                                                checked={!!deactivateBenefits[eb.id]}
                                                onChange={e => setDeactivateBenefits(prev => ({ ...prev, [eb.id]: e.target.checked }))}
                                                style={{ width: 16, height: 16, accentColor: '#dc2626', flexShrink: 0 }}
                                            />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>{eb.planName}</div>
                                                <div style={{ fontSize: 11, color: '#6b7280' }}>
                                                    {eb.type || '—'} · {eb.period || '—'} · Employee: {fmtMoney(eb.employeeDeduction || eb.employeeContributionAmount || eb.amount)}
                                                </div>
                                            </div>
                                            <span style={{ fontSize: 11, fontWeight: 600, color: deactivateBenefits[eb.id] ? '#dc2626' : '#9ca3af', flexShrink: 0 }}>
                                                {deactivateBenefits[eb.id] ? 'Deactivate' : 'Keep'}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            )}
                            {cobraBenefits.length > 0 && (
                                <div style={{ background: '#fff3cd', border: '1px solid #fcd34d', borderRadius: 10, padding: '10px 14px' }}>
                                    <div style={{ fontWeight: 700, fontSize: 12, color: '#92400e', marginBottom: 4 }}>⚠️ COBRA Notice Required</div>
                                    <div style={{ fontSize: 12, color: '#78350f' }}>
                                        This employee has qualifying health coverage. A COBRA election notice must be sent within <strong>14 days</strong> of termination. The employee's election deadline is <strong>{cobraDeadline}</strong>.
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* STEP 3 — Portal Access */}
                    {step === 3 && (
                        <div>
                            <p style={{ fontSize: 13, color: '#374151', marginBottom: 4 }}>
                                <strong>{empName}</strong> will be set to <strong>Inactive</strong> and removed from active payrolls. Choose how long they retain access to the Employee Portal to download their documents.
                            </p>
                            <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
                                The employee will receive an in-app notification with their access expiry date.
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                                {GRACE_PERIODS.map(gp => (
                                    <label key={gp.value} onClick={() => setGraceDays(gp.value)} style={{
                                        display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 16px',
                                        border: `2px solid ${graceDays === gp.value ? '#E7730A' : '#e5e7eb'}`,
                                        borderRadius: 12, cursor: 'pointer',
                                        background: graceDays === gp.value ? '#fff7ed' : '#f9fafb',
                                        transition: 'all 0.15s',
                                    }}>
                                        <div style={{
                                            width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                                            border: `2px solid ${graceDays === gp.value ? '#E7730A' : '#d1d5db'}`,
                                            background: graceDays === gp.value ? '#E7730A' : '#fff',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            {graceDays === gp.value && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: 13, color: '#374151' }}>
                                                {gp.label}
                                                {gp.value === 30 && <span style={{ marginLeft: 8, fontSize: 11, background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Recommended</span>}
                                            </div>
                                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{gp.desc}</div>
                                            {graceDays === gp.value && (
                                                <div style={{ fontSize: 12, color: '#E7730A', marginTop: 4, fontWeight: 600 }}>
                                                    Access expires: {gp.value === 0 ? fmt(termDate) : (() => { const d = new Date(termDate); d.setDate(d.getDate() + gp.value); return fmt(d.toISOString()); })()}
                                                </div>
                                            )}
                                        </div>
                                    </label>
                                ))}
                            </div>
                            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#166534' }}>
                                ✉️ <strong>Employee notification</strong> will be sent automatically after confirming — including their last day and portal access expiry date.
                            </div>
                        </div>
                    )}

                    {/* STEP 4 — Summary */}
                    {step === 4 && results && (
                        <div>
                            <div style={{ textAlign: 'center', marginBottom: 20 }}>
                                <div style={{ fontSize: 44, marginBottom: 8 }}>✅</div>
                                <div style={{ fontSize: 17, fontWeight: 700, color: '#374151' }}>Offboarding Complete</div>
                                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{empName} has been successfully offboarded.</div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 16 }}>
                                <SummaryRow label="Termination Date"    value={fmt(results.termDate)} />
                                <SummaryRow label="Reason"              value={REASONS.find(r => r.value === results.reason)?.label} />
                                <SummaryRow label="Status"              value="Set to Inactive" />
                                <SummaryRow label="Portal Access Until" value={results.graceDays === 0 ? 'Revoked immediately' : results.portalExpiryDate} />
                                <SummaryRow label="Notification Sent"   value="Yes — employee notified in-app" />
                                <SummaryRow label="Benefits Deactivated"
                                    value={results.benefitsDeactivated.length > 0 ? results.benefitsDeactivated.join(', ') : 'None selected'} />
                                {results.benefitsFailed.length > 0 && (
                                    <SummaryRow label="Benefits Failed" value={results.benefitsFailed.join(', ')} warn />
                                )}
                            </div>
                            {cobraBenefits.length > 0 && (
                                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
                                    <div style={{ fontWeight: 700, fontSize: 12, color: '#991b1b', marginBottom: 4 }}>⚠️ Action Required: COBRA Notice</div>
                                    <div style={{ fontSize: 12, color: '#7f1d1d' }}>
                                        Send COBRA election notice by <strong>{cobraDeadline}</strong>. Download the COBRA Eligibility Report from <strong>Reports → Carrier & Provider Exports</strong>.
                                    </div>
                                </div>
                            )}
                            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px' }}>
                                <div style={{ fontWeight: 600, fontSize: 12, color: '#374151', marginBottom: 8 }}>Remaining Offboarding Tasks</div>
                                {[
                                    'Collect company equipment (laptop, badge, keys)',
                                    'Remove access to external systems (email, Slack, etc.)',
                                    'Issue W-2 by January 31 of the following year',
                                    cobraBenefits.length > 0 ? `Send COBRA notice before ${cobraDeadline}` : null,
                                    results.graceDays > 0 ? `Portal access expires ${results.portalExpiryDate} — remind employee to download documents` : null,
                                ].filter(Boolean).map((item, i) => (
                                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 5, fontSize: 12, color: '#374151' }}>
                                        <span style={{ color: '#9ca3af', flexShrink: 0 }}>□</span> {item}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{ padding: '12px 24px 20px', display: 'flex', justifyContent: 'space-between', gap: 12, borderTop: '1px solid #f3f4f6' }}>
                    {step > 0 && !done
                        ? <button onClick={() => setStep(s => s - 1)} disabled={saving} style={btnSecondary}>← Back</button>
                        : <button onClick={onClose} style={btnSecondary}>{done ? 'Close' : 'Cancel'}</button>
                    }
                    {!done ? (
                        step < 3
                            ? <button onClick={() => setStep(s => s + 1)} disabled={step === 0 && !termDate} style={btnPrimary}>Continue →</button>
                            : <button onClick={handleFinish} disabled={saving} style={{ ...btnPrimary, background: saving ? '#9ca3af' : '#dc2626' }}>
                                {saving ? 'Processing…' : 'Confirm Termination'}
                              </button>
                    ) : (
                        <button onClick={() => { onDone?.(); onClose(); }} style={btnPrimary}>Done</button>
                    )}
                </div>
            </div>
        </div>
    );
}

function SummaryRow({ label, value, warn }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
            <span style={{ color: '#6b7280', flexShrink: 0 }}>{label}</span>
            <span style={{ fontWeight: 600, color: warn ? '#dc2626' : '#374151', textAlign: 'right' }}>{value}</span>
        </div>
    );
}

const labelStyle   = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
const inputStyle   = { width: '100%', padding: '9px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14, color: '#374151', outline: 'none', background: '#fff', boxSizing: 'border-box' };
const btnPrimary   = { padding: '10px 24px', background: '#E7730A', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' };
const btnSecondary = { padding: '10px 24px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' };
